import { unescapeHTML } from "library";
import { showTab } from "tabs";

let uid = 0;

export type TypewriterInfo = {
  fragment: HTMLDivElement;
  target: HTMLDivElement;
  indices: number[];
  speed: number;
  highlightedTexts: string[];
  replacements: string[];
};

export async function typewritered(owner, texts, breaker, index) {
  for (const { node, text } of texts) {
    for (let i = 0; i < text.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, breaker.speed));
      if (breaker.run) {
        // check if there is whitespace from this text and render all white space
        node.nodeValue += text[i];
      } else {
        return;
      }
    }
  }
}

export async function typewriterInverse(owner, texts, breaker, index) {
  for (const { node, text } of [...texts].reverse()) {
    for (let i = 0; i < text.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, breaker.speed));
      if (breaker.run) {
        node.nodeValue = node.nodeValue.slice(0, -1);
      } else {
        return;
      }
    }
  }
}

function getLine(tw: HTMLDivElement, lineIndex: number) {
  const { target } = parseTypewriter(tw);

  // find active code element
  const fragmentIndex = parseInt(tw.getAttribute("data-fragment-index"));
  const codeElements = Array.from(
    target.querySelectorAll("code")
  ) as HTMLDivElement[];
  let codeElement: HTMLDivElement = codeElements[0] as HTMLDivElement;

  // find the code element with the largest data-fragment-index that is smaller or equal than current fragment index
  for (const element of codeElements.slice(1)) {
    // const codeFragmentIndex = parseInt(
    //   element.getAttribute("data-fragment-index")
    // );
    // if (codeFragmentIndex <= fragmentIndex) {
    //   codeElement = element;
    // } else {
    //   break;
    // }
    if (element.classList.contains("visible")) {
      codeElement = element;
    }
    if (element.classList.contains("current-fragment")) {
      codeElement = element;
      break;
    }
  }

  if (codeElement === null) {
    return { line: null, originalContent: null, originalSource: null };
  }

  const codeLines: HTMLTableCellElement[] =
    Array.from(
      codeElement.querySelectorAll(".hljs-ln-code[data-line-number]")
    ) || [];

  const line = codeLines[lineIndex - 1];

  if (line.dataset.originalContent == null) {
    line.dataset.originalContent = line.innerHTML;
  }
  if (line.dataset.originalSource == null) {
    const targetSource =
      target.querySelector("code").dataset.source?.split("\n") || [];

    line.dataset.originalSource = targetSource[lineIndex - 1];
  }

  const { originalContent, originalSource } = line.dataset;

  return { line, originalContent, originalSource };
}

export function createTypewriters() {
  // typewriter
  const typewriters: NodeListOf<HTMLDivElement> = document.querySelectorAll(
    ".fragment.typewriter"
  );
  if (Array.from(typewriters).some((tw) => tw.__typewriter)) {
    return;
  }

  function replaceDatasource(target, index, text) {
    const code = target.querySelector("code");
    const lines = code.dataset.source.split("\n");
    lines[index] = text;
    code.dataset.source = lines.join("\n");
  }

  for (const tw of typewriters) {
    // process only once
    if (tw.__typewriterType) {
      continue;
    }

    tw.__applyTypeWriter = (target, lineIndex, replacement) => {
      replaceDatasource(target, lineIndex - 1, replacement);
    };

    // const texts = extractTypeWriter(line);
    tw.__typewriterType = async (
      lineIndex,
      process,
      index,
      highlightedText,
      replacement
    ) => {
      const { target } = parseTypewriter(tw);
      const { line, originalContent } = getLine(tw, lineIndex);

      // assign

      if (highlightedText) {
        const original = line.innerHTML;

        // temporarily set the content to the highlighted text
        line.innerHTML = highlightedText;
        let texts = extractTypeWriter(line);

        // return back to normal
        line.innerHTML = original; // originalContent;
        const originalTexts = extractTypeWriter(line, false);

        // eliminate from the left the text that is the same between originaltexts and texts
        for (let i = 0; i < originalTexts.length; i++) {
          if (originalTexts[i].text == texts[i]?.text) {
            originalTexts[i].immediate = originalTexts[i].text;
            originalTexts[i].text = "";
          }
        }

        replaceDatasource(target, lineIndex - 1, replacement);

        if (process.run) {
          await typewriterInverse(line, originalTexts, process, index);
        }

        line.innerHTML = highlightedText;

        if (process.run) {
          texts = extractTypeWriter(line);

          // eliminate the same ones
          for (let i = 0; i < originalTexts.length; i++) {
            if (originalTexts[i].immediate) {
              texts[i].node.nodeValue = originalTexts[i].immediate;
              texts[i].text = "";
            }
          }

          await typewritered(line, texts, process, index);
        }
      } else {
        line.innerHTML = originalContent;
        const texts = extractTypeWriter(line);

        return typeWriterHandler.startTypewriter(line, texts, process, index);
      }
    };
    tw.__typewriterClear = (lineIndex, replaceMent) => {
      const { target } = parseTypewriter(tw);
      const { line, originalContent, originalSource } = getLine(tw, lineIndex);

      if (replaceMent != null) {
        replaceDatasource(target, lineIndex - 1, originalSource);
        line.innerHTML = originalContent;
      } else {
        extractTypeWriter(line);
      }
    };
    tw.__typewriterFlush = (lineIndex, replacement, highlightedText) => {
      const { target } = parseTypewriter(tw);
      const { line, originalContent, originalSource } = getLine(tw, lineIndex);

      if (replacement != null) {
        replaceDatasource(target, lineIndex - 1, replacement);
        line.innerHTML = highlightedText;
      } else {
        replaceDatasource(target, lineIndex - 1, originalSource);
        line.innerHTML = originalContent;
      }
    };
  }
}

export function parseTypewriter(fragment: HTMLDivElement): TypewriterInfo {
  if (fragment.__parsedTypewriter) {
    return fragment.__parsedTypewriter;
  }
  const targetId = fragment.getAttribute("data-target");
  if (targetId == null) {
    console.error("Typewriter code is missing a target");
    return {
      fragment,
      target: null,
      indices: [],
      highlightedTexts: [],
      replacements: [],
      speed: 0,
    };
  }

  // find root section
  let section = fragment;
  while (section.tagName != "SECTION") {
    section = section.parentElement as HTMLDivElement;
  }

  const target: HTMLDivElement = section.querySelector(`#${targetId}`);
  if (target == null) {
    console.error(`Typewriter target ${targetId} not found`);
    return {
      fragment,
      target: null,
      indices: [],
      highlightedTexts: [],
      replacements: [],
      speed: 0,
    };
  }
  const propertyString = unescapeHTML(
    fragment.getAttribute("data-fragment-properties")
  );
  const properties =
    propertyString[0] === "["
      ? JSON.parse(propertyString)
      : propertyString.split("|");
  const [lines, speedString, text] = properties;

  const speed = parseInt(speedString || "25");

  // get all indices from lines, where indices can be specified as 1,2,3 or 1-3 or combined 1,2,4-7
  const trimmedLines = (lines == null ? "" : lines.toString()).trim();
  const indices = trimmedLines
    ? trimmedLines
        .trim()
        .split(",")
        .map((line) => {
          if (line.indexOf("-") > 0) {
            const parts = line.split("-");
            return Array.from({
              length: parseInt(parts[1]) - parseInt(parts[0]) + 1,
            }).map((_, i) => i + parseInt(parts[0]));
          } else {
            return parseInt(line);
          }
        })
        .flat()
    : [];

  let highlightedTexts = [];

  if (text) {
    highlightedTexts = text.split("\n").map((line) => {
      const highlight: any = Reveal.getPlugin("highlight");
      if (text.match(/<[^>]+>/)) {
        return highlight.hljs.highlight(line, {
          language: "xml",
        }).value;
      } else {
        return highlight.hljs.highlight(line, {
          language: "typescript",
        }).value;
      }
    });
  }

  fragment.__parsedTypewriter = {
    fragment,
    target,
    indices,
    speed,
    highlightedTexts,
    replacements: text?.split("\n") || [],
  };
  return fragment.__parsedTypewriter;
}

export async function applyTypewriter(element) {
  const { fragment, target, replacements, indices } = parseTypewriter(element);

  let i = 0;
  for (const index of indices) {
    fragment.__applyTypeWriter(target, index, replacements[i++]);
  }
}

export async function startTypewriters(elements, process) {
  const typerwriters = elements.map(parseTypewriter);

  if (typerwriters.length && typerwriters[0] && typerwriters[0].target) {
    showTab("tricode", typerwriters[0].target.id);

    await typeWriterHandler.startMultipleTypewriters(typerwriters, process);
  }
}

// async function startTypewriter(element) {
//   const parsed = parseTypewriter(element);

//   if (parsed.target) {
//     showTab("tricode", parsed.target.id);

//     await typeWriterHandler.startMultipleTypewriters([parsed]);
//   }
// }

function extractTypeWriter(element, replace = true) {
  const texts = [];

  function extractTexts(el) {
    if (el.nodeType === Node.TEXT_NODE) {
      texts.push({ node: el, text: el.nodeValue });
      if (replace) {
        el.nodeValue = "";
      }
    } else {
      for (const child of el.childNodes) {
        extractTexts(child);
      }
    }
  }

  extractTexts(element);
  return texts;
}

// async function typewriterFlush(texts) {
//   for (const { node, text } of texts) {
//     node.nodeValue = text;
//   }
// }

// async function clearTypewriter(element) {
//   const { target, indices, replacements } = parseTypewriter(element);

//   // execute typewriter on each line
//   let i = 0;
//   for (const index of indices) {
//     await target.__typewriter[index].__typewriterClear(replacements[i++]);
//   }
// }

export class TypeWriterHandler {
  promise: Promise<void> | null;
  processes: Array<{ run: boolean; speed: number; id: number }>;

  static getUid() {
    return uid++;
  }

  constructor() {
    this.promise = null;
    this.processes = [];
  }

  async stopTheCurrentTypewriter() {
    for (const process of this.processes) {
      process.run = false;
    }
    this.processes = [];
  }

  startTypewriter(owner, texts, process, index) {
    return typewritered(owner, texts, process, index);
  }

  async startMultipleTypewriters(
    tws: TypewriterInfo[],
    process?: { run: boolean; speed: number; id: number }
  ) {
    this.stopTheCurrentTypewriter();

    // typewriters can have multiple line replacements, we will execute them one by one

    const id = uid++;
    if (process == null) {
      process = {
        run: true,
        speed: 25,
        id,
      };
    }
    this.processes.push(process);

    // execute typewriter on each line
    for (const {
      fragment,
      target,
      indices,
      speed,
      highlightedTexts,
      replacements,
    } of tws) {
      let i = 0;
      for (const index of indices) {
        if (process.run == false) {
          return;
        }

        process.speed = speed;
        await fragment.__typewriterType(
          index,
          process,
          index,
          highlightedTexts[i],
          replacements[i]
        );
        i++;
      }
    }
  }
}

export const typeWriterHandler = new TypeWriterHandler();
